import { AbstractHttpService, sanitiseQueryParams, TextResponse, type RequestOptions } from "vue-utils"

export class LoadingError extends Error {
	title: string
	constructor(title: string, message: string) {
		super(message)
		this.title = title
	}
}

const friendlyStatusTexts: Record<number, string> = {
	400: "Invalid Data Submitted",
	401: "Invalid Login",
	403: "No Permissions",
	404: "Not Found",
	500: "Internal Server Error",
}
const statusCodeTexts: Record<number, string> = {
	400: "There was an issue with the data you submitted. Try again or contact an administrator",
	401: "Please refresh your login and try again",
	403: "You do not have sufficient permissions to perform that action",
	404: "A required value was not found. Try again or contact an administrator",
	500: "An internal server error occurred. Try again or contact an administrator",
}

export class HttpLoadingError extends LoadingError {
	readonly statusCode: number
	readonly statusText: string

	constructor(response: Response, message: string) {
		super(friendlyStatusTexts[response.status] ?? response.statusText, message)

		this.statusCode = response.status
		this.statusText = response.statusText
	}
}

export class DeciderPWAHttpService extends AbstractHttpService {
	constructor(readonly rootUrl: string) {
		super()
	}

	withRoute(url: string) {
		return new DeciderPWAHttpService(`${this.rootUrl}/${url}`)
	}

	override async fetch(request: RequestOptions): Promise<Response> {
		request = this.augmentRequest(request)
		request.headers = addHeader(request.headers, "Api-Key", "xprdB3g3O*5^*%Mag*oESRKqMuTBiG")

		let response
		try {
			response = await fetch(request.url, request)
		} catch (e) {
			console.error(e)
			throw new LoadingError("Network Error", "A network error occurred performing that action. Check your connectivity and try again")
		}
		return await this.processResponse(response)
	}

	protected async processResponse(response: Response): Promise<Response> {
		const statusCode = response.status
		if (response.ok) {
			return response
		}
		if (statusCode >= 400 && statusCode < 600) {
			const message = await tryReadJsonError(response)
			throw new HttpLoadingError(response, message ?? statusCodeTexts[statusCode] ?? "An unexpected error occurred performing that action")
		}
		throw new HttpLoadingError(response, "An unexpected error occurred performing that action")
	}

	protected augmentRequest(request: RequestOptions): RequestOptions {
		if (this.isRegularUrl(request.url)) {
			return request
		}
		return {
			...request,
			url: `${this.rootUrl}/${request.url}`,
		}
	}

	override returningText(): TextResponse {
		return super.returningText()
	}

	protected isRegularUrl(url: string) {
		return url.startsWith("/") || url.startsWith("http://") || url.startsWith("https://")
	}
}

export const Http = new DeciderPWAHttpService("/api")

async function tryReadJsonError(result: Response): Promise<string | null> {
	try {
		const errorData = (await result.json()) as { Message: string }
		return errorData.Message
	} catch (e) {
		return null
	}
}

export function createQueryUrl(url: string, params: object) {
	const paramsStr = sanitiseQueryParams(params)
	return paramsStr.length > 0 ? `${url}?${paramsStr}` : url
}

function addHeader(init: HeadersInit | undefined, key: string, value: string): Headers {
  const headers = new Headers(init);
  headers.set(key, value);
  return headers;
}